import React, { useState, useRef, useEffect } from "react";
import { Button } from "rsuite";
import { motion } from "framer-motion";
import "./styles.css";
import Container from "../../General/Container";
import freeWebinarImg from "../../../assets/images/freeWebinarImg.avif";
import speakerImg from "../../../assets/images/prateek.jpg";
import MathewPic from "../../../assets/images/people/mathew.jpeg";
import JayeshPic from "../../../assets/images/people/jayesh_arora.jpeg";
import PiyushPic from "../../../assets/images/people/Piyush.jpeg";
import AmitRaja from "../../../assets/images/people/amit_raja_speaker.jpeg";
import WebinarModal from "./WebinarModal";

const FinanceWebinar = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sectionRef = useRef(null);
  const learningRef = useRef(null);
  const detailsRef = useRef(null);
  const attendeesRef = useRef(null);
  const timerRef = useRef(null);
  const speakerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    const refs = [
      sectionRef,
      learningRef,
      detailsRef,
      attendeesRef,
      timerRef,
      speakerRef,
    ];

    refs.forEach((ref) => {
      const currentRef = ref.current;
      if (currentRef) {
        const animatedElements = currentRef.querySelectorAll(".animate");
        animatedElements.forEach((el) => observer.observe(el));
      }
    });

    return () => {
      refs.forEach((ref) => {
        const currentRef = ref.current;
        if (currentRef) {
          const animatedElements = currentRef.querySelectorAll(".animate");
          animatedElements.forEach((el) => observer.unobserve(el));
        }
      });
    };
  }, []);

  const handleRegister = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Framer Motion variants - refined for subtlety
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariant = {
    hidden: { y: 10, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.4, ease: "easeOut" },
    },
  };

  const pulseAnimation = {
    scale: [1, 1.03, 1],
    transition: { duration: 2.5, repeat: Infinity, ease: "easeInOut" },
  };

  return (
    <div className="finance-webinar-page">
      {/* Hero Section */}
      <div className="hero-section fadedBackground" ref={sectionRef}>
        <Container>
          <div className="webinar-content">
            <div className="row">
              <motion.img
                src={freeWebinarImg}
                alt="Free Webinar"
                className="free-webinar-tag"
                style={{ cursor: "pointer" }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.4 }}
                onClick={handleRegister}
              />
              <div className="col-md-7 webinar-text" style={{ margin: "auto" }}>
                <motion.h1
                  className="webinar-heading animate"
                  variants={fadeIn}
                  initial="hidden"
                  animate="visible"
                >
                  How Foundries Are{" "}
                  <span className="highlight">Growing Faster</span> <br />
                  by Fixing Credit Risk and
                  <br /> Working Capital
                </motion.h1>

                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  animate={pulseAnimation}
                  className="btn-wrapper"
                >
                  <Button
                    appearance="primary"
                    className="cta-button"
                    onClick={handleRegister}
                  >
                    View Webinar Recording
                  </Button>
                </motion.div>
              </div>

              <div className="col-md-5 webinar-image">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.7, ease: "easeOut" }}
                  style={{ cursor: "pointer" }}
                  onClick={handleRegister}
                >
                  <motion.img
                    src="https://images.nowpurchase.com/uploads/20250520105137_webinar_details_img.jpg"
                    alt="Finance Webinar"
                    whileHover={{ scale: 1.02, rotate: 0.5 }}
                    transition={{ duration: 0.4 }}
                  />
                </motion.div>
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* Learning Points Section */}
      <section className="learning-section fadedBackground" ref={learningRef}>
        <Container>
          <div className="section-header">
            <motion.h2
              className="section-title animate fade-in"
              initial="hidden"
              animate="visible"
              variants={fadeIn}
            >
              What will you Learn
            </motion.h2>
          </div>

          <motion.div
            className="learning-points-container"
            variants={staggerChildren}
            initial="hidden"
            animate="visible"
          >
            <motion.div
              className="learning-point animate"
              variants={itemVariant}
              style={{ maxWidth: "280px" }}
            >
              <div className="point-icon">✓</div>
              <div className="point-content">
                <h3>Working Capital Optimization</h3>
                <p>
                  How working capital inefficiencies are limiting your growth
                  and what to do about it
                </p>
              </div>
            </motion.div>

            <motion.div
              className="learning-point animate"
              variants={itemVariant}
              style={{ maxWidth: "280px" }}
            >
              <div className="point-icon">✓</div>
              <div className="point-content">
                <h3>Risk Reduction Strategies</h3>
                <p>
                  3 proven strategies to reduce bad debts and improve cash flow
                  in your foundry
                </p>
              </div>
            </motion.div>

            <motion.div
              className="learning-point animate"
              variants={itemVariant}
              style={{ maxWidth: "280px" }}
            >
              <div className="point-icon">✓</div>
              <div className="point-content">
                <h3>Finance as a Growth Engine</h3>
                <p>
                  Why finance isn't just support—but your next strategic growth
                  engine
                </p>
              </div>
            </motion.div>

            <motion.div
              className="learning-point animate"
              variants={itemVariant}
              style={{ maxWidth: "280px" }}
            >
              <div className="point-icon">✓</div>
              <div className="point-content">
                <h3>Real Success Stories</h3>
                <p>
                  Case studies of foundries unlocking ₹1Cr+ with smarter finance
                  habits
                </p>
              </div>
            </motion.div>
          </motion.div>
        </Container>
      </section>
      {/* Webinar Details Section */}
      {/* <section className="details-section fadedBackground" ref={detailsRef}>
        <Container>
          <div className="section-header">
            <motion.h2
              className="section-title animate fade-in"
              initial="hidden"
              animate="visible"
              variants={fadeIn}
            >
              Webinar Details
            </motion.h2>
          </div>

          <div className="details-container">
            <motion.div
              className="detail-card animate"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              style={{ maxWidth: "280px" }}
            >
              <div className="detail-icon">
                <i className="ri-user-voice-line"></i>
              </div>
              <h3>Speaker</h3>
              <p>Prateek Losalka</p>
              <span>VP - Finance</span>
            </motion.div>

            <motion.div
              className="detail-card animate"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
              style={{ maxWidth: "280px" }}
            >
              <div className="detail-icon">
                <i className="ri-calendar-check-line"></i>
              </div>
              <h3>Date</h3>
              <p>17th May</p>
              <span>Saturday</span>
            </motion.div>

            <motion.div
              className="detail-card animate"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2, ease: "easeOut" }}
              style={{ maxWidth: "280px" }}
            >
              <div className="detail-icon">
                <i className="ri-time-line"></i>
              </div>
              <h3>Time</h3>
              <p>11:30 AM - 12:30 PM</p>
              <span>IST</span>
            </motion.div>
          </div>
        </Container>
      </section> */}

      {/* About the Speaker Section */}
      <section className="speaker-section" ref={speakerRef}>
        <Container>
          <div className="section-header">
            <motion.h2
              className="section-title animate fade-in"
              initial="hidden"
              animate="visible"
              variants={fadeIn}
            >
              About the Speaker
            </motion.h2>
            <motion.div
              className="title-underline"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 100, opacity: 1 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            />
          </div>

          <div className="speaker-container">
            <motion.div
              className="speaker-image animate"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <motion.img
                src={speakerImg}
                alt="Prateek Losalka - VP Finance"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.4 }}
              />
            </motion.div>

            <motion.div
              className="speaker-bio animate"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
            >
              <h3>
                Prateek Losalka
                <a
                  href="https://www.linkedin.com/in/prateeklosalka/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="ri-linkedin-box-fill"></i>
                </a>
              </h3>
              <h4>Vice President - Finance</h4>
              <p>
                As the Finance Head at NowPurchase, a venture capital-backed
                procurement technology firm catering to the metal manufacturing
                sector, Prateek spearheads financial strategy and operations
                with a focus on driving sustainable growth and profitability.
                With over 10 years of extensive experience as a chartered
                accountant, he brings a unique blend of expertise from both
                industry and consulting roles across diverse sectors such as
                Media and Entertainment, B2B marketplaces, and consulting firms.
              </p>
              <p>
                He excels in financial analysis, reporting, and modeling,
                ensuring robust internal controls and risk management practices.
                Prateek's skill set extends to investor relations and due
                diligence, strategic budgeting and hedging strategies, as well
                as comprehensive knowledge of accounting and auditing standards.
                Passionate about streamlining financial processes through
                automation and leveraging data analytics for informed
                decision-making, he is dedicated to aligning financial
                objectives with overarching business goals.
              </p>
              <p>
                Beyond his core competencies, Prateek has a keen interest in
                exploring emerging trends and opportunities within the crypto
                and equity investment landscape.
              </p>
            </motion.div>
          </div>
          <div className="other-speakers-section">
            <motion.h4
              style={{ textAlign: "center", width: "100%" }}
              className="section-title"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              Other Speakers
            </motion.h4>
            <div className="other-speakers">
              {[
                {
                  name: "Jayesh Arora",
                  title: "Product Manager, TATA Capital",
                  img_src: JayeshPic,
                  linkedin: "https://www.linkedin.com/in/jayesharora/",
                },
                {
                  name: "Mathew Varghese Chazhoor",
                  title: "New Initiatives, Tap Capital",
                  img_src: MathewPic,
                  linkedin:
                    "https://www.linkedin.com/in/mathew-varghese-chazhoor/",
                },
                {
                  name: "Piyush Lohia",
                  title: "Founder, EaseMyRisk Advisors",
                  img_src: PiyushPic,
                  linkedin: "https://www.linkedin.com/in/piyushlohia12/",
                },
                {
                  name: "Amitrajit Sett",
                  title: "Senior Vice President, HSBC",
                  img_src: AmitRaja,
                  linkedin: "https://www.linkedin.com/in/amitrajitsetthsbc",
                },
              ].map((speaker, idx) => (
                <motion.div
                  key={idx}
                  className="other-speaker-card animate"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: idx * 0.2,
                    ease: "easeOut",
                  }}
                >
                  <div className="speaker-info">
                    <motion.img
                      src={speaker.img_src}
                      alt={speaker.name}
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.4 }}
                    />
                    <h4>{speaker.name}</h4>
                    <p>{speaker.title}</p>
                    <a
                      href={speaker.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Profile
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Who Should Attend Section */}
      <section className="attendees-section" ref={attendeesRef}>
        <Container>
          <div className="section-header">
            <motion.h2
              className="section-title animate fade-in"
              initial="hidden"
              animate="visible"
              variants={fadeIn}
            >
              Who Should Attend
            </motion.h2>
            <motion.div
              className="title-underline"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 100, opacity: 1 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            />
          </div>

          <motion.div
            className="attendees-container"
            initial="hidden"
            animate="visible"
            variants={staggerChildren}
          >
            <motion.div className="attendee-card" variants={itemVariant}>
              <h3>Foundry Owners & Directors</h3>
              <p>
                Learn how to improve your financial position and accelerate
                growth
              </p>
            </motion.div>

            <motion.div className="attendee-card" variants={itemVariant}>
              <h3>CFOs & Finance Teams</h3>
              <p>
                Discover new strategies to optimize cash flow and reduce
                financial risks
              </p>
            </motion.div>

            <motion.div className="attendee-card" variants={itemVariant}>
              <h3>Anyone Facing Financial Challenges</h3>
              <p>
                Get actionable solutions for delayed payments, stuck cash flow,
                or risky buyers
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            className="cta-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <Button
              appearance="primary"
              className="cta-button-centered"
              onClick={handleRegister}
            >
              View Webinar Recording
            </Button>
          </motion.div>
        </Container>
      </section>

      <WebinarModal
        isModalOpen={isModalOpen}
        handleCloseModal={handleCloseModal}
      />
    </div>
  );
};

export default FinanceWebinar;
